/**
 * Landing Page for MarIA Health Care
 *
 * GDPR-compliant entry point that:
 * 1. Shows welcome video and information
 * 2. Requires explicit consent checkboxes (GDPR Art. 7)
 * 3. Offers Google Sign-In OR Anonymous Auth
 * 4. Redirects to chat after successful auth + consent
 *
 * UPDATE (2026-01-13): Added Google Sign-In using Firebase modular SDK.
 * Following official Firebase pattern from:
 * https://firebase.google.com/docs/auth/web/google-signin
 *
 * @see Issue #177 - Frontend Firebase Auth Integration
 */
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Avatar interactivo desactivado por VITE_ENABLE_AVATAR. Mismo patrón que
// ChatPage — si el flag no está en "true" el módulo no entra al bundle.
const AVATAR_ENABLED = import.meta.env.VITE_ENABLE_AVATAR === "true";
const AvatarPresentation = AVATAR_ENABLED
    ? lazy(() => import('@/components/AvatarPresentation'))
    : null;

interface TextWithAudioProps {
    text: string;
}

const TextWithAudio = ({ text }: TextWithAudioProps) => {
    return (
        <div className="prose max-w-none text-foreground p-4 bg-white rounded shadow-sm">
            <p className="whitespace-pre-line">{text}</p>
        </div>
    );
};

const welcomeMessageText = `Hola, bienvenida.

Soy MarIA Health Care, tu agente virtual para la salud integral de la mujer.

Estoy aquí para acompañarte, informarte y ayudarte a resolver tus dudas sobre salud femenina de forma confidencial y personalizada.

¿Cómo puedo ayudarte hoy?
`;

/**
 * Google Icon SVG - following Google branding guidelines
 */
const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

export default function LandingPage() {
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptDataProcessing, setAcceptDataProcessing] = useState(false);
    // MarIA HC es un servicio exclusivo para mayores de 18 años.
    const [acceptAge, setAcceptAge] = useState(false);

    const { isAuthenticated, isLoading, error, initializeAuth, signInWithGoogle, clearError, consentRequired } = useAuth();
    const navigate = useNavigate();

    const [isMuted, setIsMuted] = useState(true);
    const [isInitializing, setIsInitializing] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (isAuthenticated && !isLoading && !consentRequired) {
            navigate('/chat');
        }
    }, [isAuthenticated, isLoading, consentRequired, navigate]);

    useEffect(() => {
        return () => clearError();
    }, [clearError]);

    const toggleAudio = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    /**
     * Handle Google Sign-In button click
     */
    const handleGoogleSignIn = async () => {
        if (!acceptTerms || !acceptDataProcessing || !acceptAge) return;

        setIsInitializing(true);
        clearError();

        try {
            await signInWithGoogle({
                recordConsent: true,
                retentionDays: 365,
                ageConfirmed: acceptAge
            });
            navigate('/chat');
        } catch (err) {
            console.error('Google sign-in failed:', err);
        } finally {
            setIsInitializing(false);
        }
    };

    /**
     * Handle anonymous auth button click
     */
    const handleStartAnonymous = async () => {
        if (!acceptTerms || !acceptDataProcessing || !acceptAge) return;

        setIsInitializing(true);
        clearError();

        try {
            await initializeAuth({
                recordConsent: true,
                retentionDays: 365,
                ageConfirmed: acceptAge
            });
            navigate('/chat');
        } catch (err) {
            console.error('Anonymous auth failed:', err);
        } finally {
            setIsInitializing(false);
        }
    };

    const canProceed = acceptTerms && acceptDataProcessing && acceptAge && !isInitializing && !isLoading;

    return (
        <div className="min-h-screen bg-background flex flex-col font-sans">
            <Navbar />

            <main className="flex-1 w-full max-w-5xl mx-auto p-4 md:p-8 flex flex-col items-center">

                <Card className="w-full shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-6 flex flex-col items-center">

                        {/* Hero Video Area */}
                        <div className="w-full max-w-3xl overflow-hidden rounded-xl shadow-md mb-6 relative aspect-video bg-black">
                            <video
                                ref={videoRef}
                                src="/video_inicial.mp4"
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            <Button
                                size="sm"
                                variant="secondary"
                                className="absolute bottom-4 right-4 bg-black/50 text-white hover:bg-black/70 backdrop-blur"
                                onClick={toggleAudio}
                            >
                                {isMuted ? "Activar Audio" : "Silenciar"}
                            </Button>
                        </div>

                        {/* Content Area */}
                        <div className="w-full max-w-3xl space-y-6">
                            {AvatarPresentation ? (
                                <Suspense fallback={<TextWithAudio text={welcomeMessageText} />}>
                                    <AvatarPresentation
                                        onComplete={() => { }}
                                        welcomeMessage={welcomeMessageText}
                                    />
                                </Suspense>
                            ) : (
                                <TextWithAudio
                                    text={welcomeMessageText}
                                />
                            )}

                            {/* Error Alert */}
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            {/* GDPR Consent Checkboxes */}
                            <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                                <h3 className="text-sm font-semibold text-foreground mb-3">
                                    Consentimiento requerido (GDPR)
                                </h3>

                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="terms"
                                        checked={acceptTerms}
                                        onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                                        disabled={isInitializing}
                                    />
                                    <label
                                        htmlFor="terms"
                                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                        He leído y acepto las{' '}
                                        <a
                                            href="/legal/condiciones.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline font-medium"
                                        >
                                            condiciones del programa piloto
                                        </a>
                                        . Entiendo que eres una agente virtual y no sustituyes el consejo de un/a profesional sanitario/a.
                                    </label>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="data-processing"
                                        checked={acceptDataProcessing}
                                        onCheckedChange={(checked) => setAcceptDataProcessing(checked === true)}
                                        disabled={isInitializing}
                                    />
                                    <label
                                        htmlFor="data-processing"
                                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                        Consiento el tratamiento de mis datos de salud de forma{' '}
                                        <strong>cifrada y pseudonimizada</strong> para recibir información personalizada.
                                        Mis datos se conservarán por un máximo de 365 días y puedo solicitar su eliminación
                                        en cualquier momento (GDPR Art. 17).{' '}
                                        <a
                                            href="/legal/privacidad.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline font-medium"
                                        >
                                            Política de privacidad
                                        </a>
                                    </label>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Checkbox
                                        id="age-confirmed"
                                        checked={acceptAge}
                                        onCheckedChange={(checked) => setAcceptAge(checked === true)}
                                        disabled={isInitializing}
                                    />
                                    <label
                                        htmlFor="age-confirmed"
                                        className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                        Confirmo que tengo <strong>18 años o más</strong>. MarIA HC es un servicio
                                        exclusivo para personas mayores de edad.
                                    </label>
                                </div>
                            </div>

                            {/* Sign-In Options */}
                            <div className="flex flex-col items-center gap-4 pt-4">
                                {/* Google Sign-In Button */}
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-80 text-base py-6 bg-white hover:bg-gray-50 border-gray-300"
                                    disabled={!canProceed}
                                    onClick={handleGoogleSignIn}
                                >
                                    {isInitializing ? (
                                        <span className="flex items-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></span>
                                            Iniciando...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-3">
                                            <GoogleIcon />
                                            Continuar con Google
                                        </span>
                                    )}
                                </Button>

                                {/* Aclaración login Google: la personalización por memoria solo aplica con cuenta */}
                                <p className="text-xs text-center text-muted-foreground w-full sm:w-80 -mt-2">
                                    (Solo si quieres respuestas personalizadas)
                                </p>

                                {/* Divider */}
                                <div className="flex items-center gap-4 w-full sm:w-80">
                                    <div className="flex-1 border-t border-muted"></div>
                                    <span className="text-xs text-muted-foreground">o</span>
                                    <div className="flex-1 border-t border-muted"></div>
                                </div>

                                {/* Anonymous Button */}
                                <Button
                                    variant="secondary"
                                    className="w-full sm:w-80 text-base py-6"
                                    disabled={!canProceed}
                                    onClick={handleStartAnonymous}
                                >
                                    {isInitializing ? (
                                        <span className="flex items-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></span>
                                            Iniciando...
                                        </span>
                                    ) : (
                                        'Comenzar sin cuenta'
                                    )}
                                </Button>
                            </div>

                            {/* Privacy Note */}
                            <p className="text-xs text-center text-muted-foreground">
                                Con Google guardamos tu historial de forma segura.
                                Sin cuenta, usamos autenticación anónima (se pierde al cerrar el navegador).
                            </p>

                        </div>

                    </CardContent>
                </Card>

            </main>

            <footer className="p-6 text-center text-sm text-muted-foreground">
                © {new Date().getFullYear()} MarIA Health Care - Proyecto de Investigación Universitaria
            </footer>
        </div>
    );
}
